import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Upload, Film, RefreshCw, Save, Eraser, Play, ChevronRight, Loader2,
  AlertTriangle, BookMarked, Clapperboard, ArrowDownToLine,
} from "lucide-react";
import { api } from "@/api/client";
import { pipelinesApi } from "@/api/mist";

const SEEDS = [
  { key: "last", label: "Last frame", hint: "Continue from where the clip ends (default)." },
  { key: "first", label: "First frame", hint: "Restart the motion from the clip's opening." },
  { key: "custom", label: "Custom time", hint: "Seed from an exact second of the clip." },
];
const STAGES = ["Preparing seed", "Extracting frame", "Sending to generator", "Rendering shot", "Finalising output"];
const EMPTY_BIBLE = { title: "", hero: "", outfit: "", environment: "", threat: "", palette: "", style: "scifi", motion: "" };
const F = "border-2 border-[#2a2a2a] bg-[#0e0e0e] text-cream font-mono text-[12px] px-3 py-2 w-full placeholder:text-white/25 focus:border-gold outline-none";

// Sniff the first bytes of a file to positively detect a video container.
// Browsers report an EMPTY File.type for extensionless files (MIME is guessed
// from the extension, never by content), so a real MP4 named "clip" with no
// extension has type="" and would be wrongly rejected by a type/extension gate.
async function looksLikeVideo(file) {
  try {
    const b = new Uint8Array(await file.slice(0, 16).arrayBuffer());
    if (b[4] === 0x66 && b[5] === 0x74 && b[6] === 0x79 && b[7] === 0x70) return true; // MP4/MOV 'ftyp'
    if (b[0] === 0x1a && b[1] === 0x45 && b[2] === 0xdf && b[3] === 0xa3) return true; // WEBM/MKV EBML
    if (b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46) return true; // AVI 'RIFF'
    if (b[0] === 0x00 && b[1] === 0x00 && b[2] === 0x01 && b[3] === 0xba) return true; // MPEG-PS
    if (b[0] === 0x47) return true;                                                    // MPEG-TS sync
    return false;
  } catch { return false; }
}

// Authed blob video player (vault assets are already persisted).
function ClipVideo({ assetId }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let obj;
    if (assetId) {
      api.get(`/central-core/vault-file/${assetId}`, { responseType: "blob" })
        .then((r) => { obj = URL.createObjectURL(r.data); setSrc(obj); }).catch(() => {});
    }
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [assetId]);
  if (!src) return <div className="w-full h-44 grid place-items-center bg-black border-2 border-[#2a2a2a]"><Loader2 size={16} className="text-gold animate-spin" /></div>;
  return <video src={src} controls playsInline data-testid="continue-output-video" className="w-full max-h-72 bg-black border-2 border-[#2a2a2a]" />;
}

export default function ContinueShot() {
  const [source, setSource] = useState(null);      // {asset_id, name, duration, tag}
  const [uploading, setUploading] = useState(false);
  const [frame, setFrame] = useState("last");
  const [customSec, setCustomSec] = useState("");
  const [bibles, setBibles] = useState([]);
  const [bibleId, setBibleId] = useState("");
  const [biblesLoading, setBiblesLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_BIBLE);
  const [prompt, setPrompt] = useState("");
  const [adherence, setAdherence] = useState("balanced");
  const [gen, setGen] = useState(false);
  const [stage, setStage] = useState(0);
  const [output, setOutput] = useState(null);
  const [chain, setChain] = useState([]);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const loadBibles = async () => {
    setBiblesLoading(true);
    try { const d = await pipelinesApi.getSceneBibles(); setBibles(d.bibles || []); }
    catch { toast.error("Couldn't load Scene Bibles"); }
    finally { setBiblesLoading(false); }
  };
  useEffect(() => { loadBibles(); }, []);

  const onUpload = async (e) => {
    const f = (e.target.files || [])[0]; e.target.value = "";
    if (!f) return;
    const t = (f.type || "").toLowerCase();
    const extVideo = /\.(mp4|mov|webm|m4v|mpe?g|mkv|avi|3gp|mts|m2ts|hevc|qt)$/i.test(f.name || "");
    // Accept a real video by MIME, by extension, or by sniffing header bytes
    // (covers extensionless / octet-stream device clips whose type is empty).
    const ok = t.startsWith("video/") || extVideo || (await looksLikeVideo(f));
    // Only block when we have POSITIVE evidence the file is non-video.
    if (!ok && t && !t.startsWith("video/")) {
      setError("That file isn't a video — pick an MP4, MOV, WEBM or similar clip."); return;
    }
    setUploading(true); setError(null);
    try {
      // Normalise the name/type when it lacks a video extension/MIME so the
      // backend's video check always passes for a genuine clip.
      const up = (!extVideo && !t.startsWith("video/"))
        ? new File([f], `clip_${Date.now()}.mp4`, { type: "video/mp4" })
        : f;
      const r = await pipelinesApi.uploadSeedVideo(up);
      setSource({ asset_id: r.asset_id, name: f.name || up.name, duration: r.duration || null, tag: "uploaded" });
      toast.success("Clip uploaded");
    } catch (err) { setError(err?.response?.data?.detail || "Upload failed — try a smaller clip."); }
    finally { setUploading(false); }
  };

  const selectedBible = bibles.find((b) => b.bible_id === bibleId) || null;
  const loadIntoForm = (b) => setForm({ ...EMPTY_BIBLE, ...b, title: b.title || "" });
  const saveBible = async () => {
    if (!form.title.trim()) { setError("Give the Scene Bible a title before saving."); return; }
    try {
      const d = await pipelinesApi.saveSceneBible(form);
      toast.success("Scene Bible saved");
      await loadBibles(); setBibleId(d.bible_id);
    } catch (err) { setError(err?.response?.data?.detail || "Couldn't save the Bible."); }
  };

  const secNum = parseFloat(customSec);
  const customValid = frame !== "custom" || (!isNaN(secNum) && secNum >= 0 && (!source?.duration || secNum <= source.duration));
  const canGen = !!source && !!frame && !!prompt.trim() && customValid && !gen;

  const generate = async () => {
    if (!canGen) return;
    setGen(true); setError(null); setStage(0);
    const tick = setInterval(() => setStage((s) => Math.min(s + 1, STAGES.length - 1)), 9000);
    try {
      const payload = {
        source_asset_id: source.asset_id, scene: prompt.trim(),
        frame: frame === "custom" ? String(secNum) : frame,
        adherence, style: form.style || null, bible_id: bibleId || null,
        duration: 6, character_lock: true,
      };
      const { job_id } = await pipelinesApi.chainScene(payload);
      let done = null;
      for (let i = 0; i < 90; i++) {
        await new Promise((r) => setTimeout(r, 4000));
        const j = await pipelinesApi.jobStatus(job_id);
        if (j.status === "completed" && j.asset_id) { done = j; break; }
        if (j.status === "failed") throw new Error(j.error || "Render failed");
      }
      if (!done) throw new Error("Render timed out — try again");
      const clip = { asset_id: done.asset_id, title: `Shot ${chain.length + 1}`, engine: done.engine, order: chain.length + 1 };
      setOutput(clip);
      setChain((c) => [...c, clip]);
      toast.success("Continued shot ready");
    } catch (err) { setError(err.message || "Generation failed"); }
    finally { clearInterval(tick); setGen(false); }
  };

  const promoteToSource = (clip) => {
    setSource({ asset_id: clip.asset_id, name: clip.title, duration: 6, tag: "generated" });
    toast.success("Promoted to source — describe the next shot");
  };

  return (
    <div className="h-full w-full overflow-y-auto no-scrollbar bg-black px-4 py-4 max-w-[412px] mx-auto" data-testid="continue-shot-panel">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Clapperboard size={18} className="text-gold" />
        <h1 className="font-serif text-2xl text-white leading-none flex-1">Director / Continue Shot</h1>
        <span data-testid="continue-preview-badge" className="font-mono text-[8px] font-black uppercase tracking-widest text-black bg-gold border-2 border-black px-1.5 py-0.5">Preview</span>
      </div>
      <p className="mt-2 font-mono text-[10px] leading-relaxed text-white/50">Chain the next shot from an existing clip while preserving subject, outfit, palette, environment &amp; motion.</p>

      {error && (
        <div data-testid="continue-error" className="mt-3 flex items-start gap-2 border-2 border-[#FF5C7A] bg-[#FF5C7A]/10 px-3 py-2 shadow-[4px_4px_0px_#000]">
          <AlertTriangle size={14} className="text-[#FF5C7A] shrink-0 mt-0.5" />
          <p className="font-mono text-[11px] text-cream flex-1">{error}</p>
          <button onClick={() => setError(null)} className="font-mono text-[10px] text-white/50">✕</button>
        </div>
      )}

      {/* 1) Source clip */}
      <Section n="1" title="Source Clip">
        <input ref={fileRef} type="file" accept="video/*" onChange={onUpload} className="hidden" data-testid="continue-upload-input" />
        <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="continue-upload-btn"
          className="w-full flex items-center justify-center gap-2 border-2 border-[#2a2a2a] bg-[#141414] text-cream font-mono text-[11px] font-black uppercase tracking-widest px-3 py-2.5 shadow-[4px_4px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-50">
          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {uploading ? "Uploading…" : "Upload MP4"}
        </button>
        {source && (
          <div data-testid="continue-source-card" className="mt-2 border-2 border-gold bg-[#0e0e0e] p-2.5 shadow-[4px_4px_0px_#000]">
            <div className="flex items-center gap-2">
              <Film size={14} className="text-gold shrink-0" />
              <p className="font-mono text-[11px] text-cream break-all flex-1 min-w-0">{source.name}</p>
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {source.duration != null && <Badge>{source.duration}s</Badge>}
              <Badge>{source.tag}</Badge>
            </div>
          </div>
        )}
      </Section>

      {/* 2) Frame seed */}
      <Section n="2" title="Frame Seed">
        <div className="grid grid-cols-3 gap-1.5">
          {SEEDS.map((s) => (
            <button key={s.key} onClick={() => setFrame(s.key)} data-testid={`continue-seed-${s.key}`}
              className={`border-2 px-1 py-2 font-mono text-[10px] font-black uppercase shadow-[3px_3px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none ${frame === s.key ? "border-gold bg-gold text-black" : "border-[#2a2a2a] bg-[#141414] text-cream"}`}>
              {s.label}
            </button>
          ))}
        </div>
        <p className="mt-1.5 font-mono text-[9px] text-white/40">{SEEDS.find((s) => s.key === frame).hint}</p>
        {frame === "custom" && (
          <input type="number" min="0" step="0.1" value={customSec} onChange={(e) => setCustomSec(e.target.value)}
            placeholder={`seconds${source?.duration ? ` (0–${source.duration})` : ""}`} data-testid="continue-custom-sec"
            className={`mt-2 ${F} ${!customValid ? "border-[#FF5C7A]" : ""}`} />
        )}
        <div className="mt-2"><Badge>{frame === "custom" ? `@ ${customSec || "?"}s` : `${frame} frame`}</Badge></div>
      </Section>

      {/* 3) Scene Bible select */}
      <Section n="3" title="Scene Bible" right={
        <button onClick={loadBibles} data-testid="continue-bible-refresh" className="flex items-center gap-1 font-mono text-[9px] text-gold"><RefreshCw size={11} className={biblesLoading ? "animate-spin" : ""} /> Refresh</button>
      }>
        <select value={bibleId} onChange={(e) => setBibleId(e.target.value)} data-testid="continue-bible-select" className={F}>
          <option value="">— No Bible (freeform) —</option>
          {bibles.map((b) => <option key={b.bible_id} value={b.bible_id}>{b.title || "Untitled"}</option>)}
        </select>
        {selectedBible && (
          <div data-testid="continue-bible-summary" className="mt-2 border-2 border-[#2a2a2a] bg-[#0e0e0e] p-2.5 space-y-1">
            {["hero", "outfit", "environment", "threat", "palette", "style", "motion"].map((k) => selectedBible[k] && (
              <p key={k} className="font-mono text-[10px] text-cream"><span className="text-gold uppercase">{k}: </span>{selectedBible[k]}</p>
            ))}
            <button onClick={() => loadIntoForm(selectedBible)} className="mt-1 font-mono text-[9px] text-gold underline" data-testid="continue-bible-load">Load into editor ↓</button>
          </div>
        )}
      </Section>

      {/* 4) Scene Bible editor */}
      <Section n="4" title="Scene Bible Editor" icon={<BookMarked size={13} className="text-gold" />}>
        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Bible title (e.g. MiST)" className={`${F} mb-1.5`} data-testid="continue-bible-title" />
        {["hero", "outfit", "environment", "threat", "palette", "motion"].map((k) => (
          <textarea key={k} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} rows={2}
            placeholder={k[0].toUpperCase() + k.slice(1)} className={`${F} mb-1.5 resize-none`} data-testid={`continue-bible-${k}`} />
        ))}
        <input value={form.style} onChange={(e) => setForm({ ...form, style: e.target.value })} placeholder="Style (e.g. scifi)" className={`${F} mb-2`} data-testid="continue-bible-style" />
        <div className="flex gap-1.5">
          <button onClick={saveBible} data-testid="continue-bible-save" className="flex-1 flex items-center justify-center gap-1.5 border-2 border-black bg-gold text-black font-mono text-[10px] font-black uppercase px-2 py-2 shadow-[4px_4px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><Save size={12} /> Save New</button>
          <button onClick={() => setForm(EMPTY_BIBLE)} data-testid="continue-bible-clear" className="flex items-center justify-center gap-1.5 border-2 border-[#2a2a2a] bg-[#141414] text-cream font-mono text-[10px] font-black uppercase px-3 py-2 shadow-[4px_4px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><Eraser size={12} /> Clear</button>
        </div>
      </Section>

      {/* 5) Next shot prompt */}
      <Section n="5" title="Next Shot">
        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3} data-testid="continue-prompt"
          placeholder="Describe the next shot — action, camera move, what changes. Keep continuity language (same hero, same suit)…" className={`${F} resize-none`} />
        <div className="mt-1 flex justify-between">
          <span className="font-mono text-[9px] text-white/35">Tip: name the motion &amp; who's in frame.</span>
          <span className="font-mono text-[9px] text-white/35">{prompt.length} chars</span>
        </div>
      </Section>

      {/* 6) Generate */}
      <button onClick={generate} disabled={!canGen} data-testid="continue-generate-btn"
        className={`mt-4 w-full flex items-center justify-center gap-2 border-2 px-3 py-3.5 font-mono text-[12px] font-black uppercase tracking-widest shadow-[4px_4px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none ${canGen ? "border-black bg-gold text-black" : "border-[#2a2a2a] bg-[#141414] text-white/30"}`}>
        {gen ? <Loader2 size={15} className="animate-spin" /> : <Play size={15} />} {gen ? STAGES[stage] + "…" : "Generate Continued Shot"}
      </button>
      {gen && <div className="mt-2 border-2 border-[#2a2a2a] bg-[#0e0e0e] px-3 py-2" data-testid="continue-progress"><p className="font-mono text-[10px] text-gold">{STAGES[stage]} — this takes ~30–60s, don't leave the panel.</p></div>}

      {/* 7) Output preview */}
      {output && (
        <Section n="7" title="Output">
          <ClipVideo assetId={output.asset_id} />
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            <Badge>1080×1920</Badge><Badge>~6s</Badge>{output.engine && <Badge>{output.engine}</Badge>}
          </div>
          <button onClick={() => promoteToSource(output)} data-testid="continue-use-as-source"
            className="mt-2 w-full flex items-center justify-center gap-2 border-2 border-black bg-gold text-black font-mono text-[10px] font-black uppercase tracking-widest px-3 py-2.5 shadow-[4px_4px_0px_#000] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
            <ArrowDownToLine size={13} /> Use This Result As Next Source
          </button>
        </Section>
      )}

      {/* 8) Filmstrip */}
      {chain.length > 0 && (
        <Section n="8" title={`Chain (${chain.length})`}>
          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1" data-testid="continue-filmstrip">
            {chain.map((c) => (
              <div key={c.asset_id} data-testid={`continue-chain-${c.order}`} className={`shrink-0 w-32 border-2 ${output?.asset_id === c.asset_id ? "border-gold" : "border-[#2a2a2a]"} bg-[#0e0e0e] p-1.5 shadow-[3px_3px_0px_#000]`}>
                <button onClick={() => setOutput(c)} className="w-full text-left">
                  <div className="h-20 bg-black border border-[#2a2a2a] grid place-items-center"><Film size={16} className="text-white/40" /></div>
                  <p className="mt-1 font-mono text-[9px] font-black text-cream">#{c.order} · {c.title}</p>
                </button>
                <button onClick={() => promoteToSource(c)} data-testid={`continue-from-${c.order}`} className="mt-1 w-full flex items-center justify-center gap-1 font-mono text-[8px] font-black uppercase text-gold border border-gold/50 py-1 active:opacity-70">
                  Continue <ChevronRight size={9} />
                </button>
              </div>
            ))}
          </div>
        </Section>
      )}
      <div className="h-6" />
    </div>
  );
}

function Section({ n, title, right, icon, children }) {
  return (
    <div className="mt-4">
      <div className="flex items-center gap-2 mb-1.5">
        <span className="font-mono text-[9px] font-black text-black bg-gold border-2 border-black w-5 h-5 grid place-items-center">{n}</span>
        {icon}
        <h2 className="font-serif text-base text-white flex-1">{title}</h2>
        {right}
      </div>
      {children}
    </div>
  );
}
function Badge({ children }) {
  return <span className="font-mono text-[8px] font-black uppercase tracking-widest text-gold bg-[#141414] border-2 border-[#2a2a2a] px-1.5 py-0.5">{children}</span>;
}
